import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
    Leaf, ArrowLeft, EnvelopeSimple, Lock, CheckCircle, Warning,
    Eye, EyeSlash, ShieldCheck, Spinner, ArrowClockwise
} from '@phosphor-icons/react';

function formatError(detail) {
    if (!detail) return 'Something went wrong. Please try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
    return detail?.msg || String(detail);
}

export default function AccountSettingsPage() {
    const { user, updateEmail, updatePassword, verifyEmailCode, resendVerification } = useAuth();

    // Email form
    const [newEmail, setNewEmail] = useState('');
    const [emailPassword, setEmailPassword] = useState('');
    const [emailSaving, setEmailSaving] = useState(false);
    const [awaitingCode, setAwaitingCode] = useState(false);
    const [verifyCode, setVerifyCode] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [resending, setResending] = useState(false);

    // Password form
    const [currentPw, setCurrentPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [pwSaving, setPwSaving] = useState(false);
    const [showPw, setShowPw] = useState(false);

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (!newEmail.trim() || !emailPassword) { toast.error('Fill in both fields'); return; }
        setEmailSaving(true);
        try {
            await updateEmail(newEmail.trim(), emailPassword);
            toast.success('Email updated! Check your new inbox for a code.');
            setAwaitingCode(true);
            setNewEmail('');
            setEmailPassword('');
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail));
        } finally {
            setEmailSaving(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        if (!verifyCode.trim()) { toast.error('Enter the 6-digit code'); return; }
        setVerifying(true);
        try {
            await verifyEmailCode(verifyCode.trim());
            toast.success('Email verified!');
            setAwaitingCode(false);
            setVerifyCode('');
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail));
        } finally {
            setVerifying(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        try {
            await resendVerification();
            toast.success('A new code is on its way to ' + user?.email);
            setAwaitingCode(true);
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail));
        } finally {
            setResending(false);
        }
    };

    const handlePwSubmit = async (e) => {
        e.preventDefault();
        if (newPw.length < 6) { toast.error('New password must be at least 6 characters'); return; }
        if (newPw !== confirmPw) { toast.error('New passwords do not match'); return; }
        setPwSaving(true);
        try {
            await updatePassword(currentPw, newPw);
            toast.success('Password updated successfully!');
            setCurrentPw(''); setNewPw(''); setConfirmPw('');
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail));
        } finally {
            setPwSaving(false);
        }
    };

    const inputCls = "w-full px-4 py-3 rounded-xl border-3 border-[#0f172a] font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-[#22c55e] bg-white";

    return (
        <div className="min-h-screen bg-[#fefce8] dot-grid">
            {/* Nav */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#0f172a]">
                <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                        <div className="w-9 h-9 rounded-full bg-[#22c55e] border-2 border-[#0f172a] flex items-center justify-center">
                            <Leaf weight="fill" className="text-white" size={18} />
                        </div>
                        <span className="font-fredoka text-lg font-bold text-[#0f172a]">Bush Rangers</span>
                    </Link>
                    <Link to="/dashboard" className="flex items-center gap-2 text-[#475569] hover:text-[#22c55e] font-semibold" data-testid="back-to-dashboard">
                        <ArrowLeft weight="bold" size={20} />
                        <span className="hidden sm:inline">Dashboard</span>
                    </Link>
                </div>
            </nav>

            <main className="max-w-3xl mx-auto px-4 pt-24 pb-8" data-testid="account-settings-page">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                    <h1 className="font-fredoka text-3xl font-bold text-[#0f172a] flex items-center gap-2">
                        <ShieldCheck weight="duotone" className="text-[#22c55e]" size={36} />
                        Account Settings
                    </h1>
                    <p className="text-[#475569] mt-1">Update your email address and password.</p>
                </motion.div>

                {/* Email card */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                    className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-6 mb-6"
                    data-testid="email-card"
                >
                    <h2 className="font-fredoka text-xl font-bold text-[#0f172a] flex items-center gap-2 mb-1">
                        <EnvelopeSimple weight="duotone" className="text-[#38bdf8]" size={24} />
                        Email Address
                    </h2>
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                        <span className="text-sm text-[#475569]">Current:</span>
                        <span className="font-bold text-[#0f172a] text-sm" data-testid="current-email">{user?.email}</span>
                        {user?.email_verified
                            ? <span className="inline-flex items-center gap-1 text-xs font-bold text-[#16a34a] bg-[#22c55e]/10 px-2 py-0.5 rounded-full" data-testid="email-verified-badge"><CheckCircle weight="fill" size={14} /> Verified</span>
                            : <span className="inline-flex items-center gap-1 text-xs font-bold text-[#b45309] bg-[#f59e0b]/10 px-2 py-0.5 rounded-full" data-testid="email-unverified-badge"><Warning weight="fill" size={14} /> Not verified</span>}
                    </div>

                    {!awaitingCode && (
                        <form onSubmit={handleEmailSubmit} className="space-y-3">
                            <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="New email address" className={inputCls} data-testid="new-email-input" />
                            <input type="password" value={emailPassword} onChange={e => setEmailPassword(e.target.value)} placeholder="Current password (to confirm)" className={inputCls} data-testid="email-password-input" />
                            <button type="submit" disabled={emailSaving} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-4 border-[#0f172a] bg-[#38bdf8] text-white font-fredoka font-bold shadow-[3px_3px_0px_#0f172a] disabled:opacity-50 hover:-translate-y-0.5 transition-transform" data-testid="update-email-btn">
                                {emailSaving ? <Spinner className="animate-spin" size={20} /> : 'Update Email'}
                            </button>
                        </form>
                    )}

                    {(awaitingCode || !user?.email_verified) && (
                        <form onSubmit={handleVerify} className={`space-y-3 bg-[#f8fafc] border-2 border-dashed border-[#0f172a]/30 rounded-xl p-4 ${!awaitingCode ? 'mt-4' : ''}`} data-testid="verify-panel">
                            <p className="text-sm text-[#475569]">
                                {awaitingCode
                                    ? <>We sent a 6-digit code to <strong>{user?.email}</strong>. Enter it below to verify.</>
                                    : <>Your email <strong>{user?.email}</strong> isn't verified yet. Send a code and enter it below.</>}
                            </p>
                            <input value={verifyCode} onChange={e => setVerifyCode(e.target.value)} placeholder="6-digit code" maxLength={6} className={inputCls} data-testid="verify-code-input" />
                            <div className="flex gap-2 flex-wrap">
                                <button type="submit" disabled={verifying} className="flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-4 border-[#0f172a] bg-[#22c55e] text-white font-fredoka font-bold shadow-[3px_3px_0px_#0f172a] disabled:opacity-50" data-testid="verify-code-btn">
                                    {verifying ? <Spinner className="animate-spin" size={20} /> : 'Verify'}
                                </button>
                                <button type="button" onClick={handleResend} disabled={resending} className="px-4 py-3 rounded-xl border-4 border-[#0f172a] bg-[#fde047] text-[#0f172a] font-fredoka font-bold shadow-[3px_3px_0px_#0f172a] disabled:opacity-50 flex items-center gap-1.5" data-testid="resend-code-btn">
                                    {resending ? <Spinner className="animate-spin" size={18} /> : <ArrowClockwise weight="bold" size={18} />} Resend
                                </button>
                                {awaitingCode && (
                                    <button type="button" onClick={() => setAwaitingCode(false)} className="px-4 py-3 rounded-xl border-4 border-[#0f172a] bg-white font-fredoka font-bold" data-testid="verify-later-btn">Later</button>
                                )}
                            </div>
                        </form>
                    )}
                </motion.section>

                {/* Password card */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                    className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-6"
                    data-testid="password-card"
                >
                    <h2 className="font-fredoka text-xl font-bold text-[#0f172a] flex items-center gap-2 mb-4">
                        <Lock weight="duotone" className="text-[#f59e0b]" size={24} />
                        Password
                    </h2>
                    <form onSubmit={handlePwSubmit} className="space-y-3">
                        <input type={showPw ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)} placeholder="Current password" className={inputCls} data-testid="current-password-input" />
                        <input type={showPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="New password (min 6 characters)" className={inputCls} data-testid="new-password-input" />
                        <input type={showPw ? 'text' : 'password'} value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Confirm new password" className={inputCls} data-testid="confirm-password-input" />
                        <label className="flex items-center gap-2 text-sm text-[#475569] font-semibold cursor-pointer">
                            <button type="button" onClick={() => setShowPw(v => !v)} className="p-1" data-testid="toggle-password-visibility">
                                {showPw ? <EyeSlash weight="bold" size={18} /> : <Eye weight="bold" size={18} />}
                            </button>
                            Show passwords
                        </label>
                        <button type="submit" disabled={pwSaving} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-4 border-[#0f172a] bg-[#f59e0b] text-white font-fredoka font-bold shadow-[3px_3px_0px_#0f172a] disabled:opacity-50 hover:-translate-y-0.5 transition-transform" data-testid="update-password-btn">
                            {pwSaving ? <Spinner className="animate-spin" size={20} /> : 'Update Password'}
                        </button>
                    </form>
                </motion.section>
            </main>
        </div>
    );
}
